// Übersicht der Tastenkürzel (Taste „?“) und die globalen Kürzel mit Modifikator, die überall wirken (auch beim Tippen):
//   ⌘⇧L  Neuer Lead
//   ⌘.   Auflegen (läuft ein Anruf), sonst den Power Dialer pausieren (wie Close)
//   ⌘⇧X  Power Dialer starten (auf einer Smart View, „weiter“ mit den noch nicht Erreichten) bzw. nächster Anruf
//   ⌘⇧S  Stumm ein/aus (im Gespräch), ⌘⇧D Tastenfeld im Gespräch, ⌘⇧Y Nummer wählen (Code in `telefonie/`)
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTelefonie } from "../telefonie";
import { ebene, modTaste } from "../werkzeuge/tasten";
import { Dialog } from "./Dialog";
import { useMeldung } from "./Meldungen";
import { neuerLeadOeffnen } from "./smartview/dialogBus";

const LISTE: [string, [string, string][]][] = [
  [
    "Überall",
    [
      [`${modTaste} K  oder  /`, "Suchen und Befehle"],
      ["?", "Diese Übersicht"],
      ["g i", "Zur Inbox"],
      ["g l", "Zu allen Leads"],
      [`${modTaste} ⇧ L`, "Neuen Lead anlegen"],
      [`${modTaste} S`, "Seitenleiste ein- und ausklappen"],
    ],
  ],
  [
    "Telefon",
    [
      [`${modTaste} .`, "Auflegen, ohne Anruf den Power Dialer pausieren"],
      [`${modTaste} ⇧ X`, "Smart View: Power Dialer starten oder fortsetzen. Im Dialer: nächster Anruf"],
      [`${modTaste} ⇧ S`, "Im Gespräch: Stumm ein- und ausschalten"],
      [`${modTaste} ⇧ D`, "Im Gespräch: Tastenfeld ein- und ausblenden"],
      [`${modTaste} ⇧ Y`, "Nummer wählen (auch ohne Lead)"],
    ],
  ],
  [
    "Listen (Smart View, Leads, Inbox, Kontakte, Aktivitäten)",
    [
      ["j / ↓", "Nächste Zeile"],
      ["k / ↑", "Vorherige Zeile"],
      ["Enter", "Lead öffnen"],
      ["x", "Zeile markieren oder abwählen"],
      ["Esc", "Markierung aufheben"],
      ["f", "In der Liste suchen (Esc leert die Suche)"],
    ],
  ],
  [
    "Inbox",
    [
      ["x", "Eintrag markieren oder abwählen"],
      ["e  oder  y", "Erledigt (die Markierten, sonst die Zeile)"],
      [`${modTaste} Enter`, "Lead in neuem Fenster öffnen"],
      ["Esc", "Auswahl aufheben"],
    ],
  ],
  [
    "Lead-Seite",
    [
      ["j", "Nächster Lead der Liste"],
      ["k", "Vorheriger Lead der Liste"],
      ["Esc", "Zurück zur Liste"],
      ["n", "Notiz schreiben"],
      ["e", "E-Mail schreiben"],
      ["t", "SMS schreiben"],
      ["a", "Custom Activity wählen"],
      ["f", "Follow-up für morgen anlegen"],
      ["s", "Status ändern"],
      ["c", "Hauptnummer anrufen"],
      [`${modTaste} Enter`, "Notiz bzw. Custom Activity speichern, E-Mail bzw. SMS senden"],
    ],
  ],
];

/** Smart-View-Seiten, die keine einzelne Ansicht sind (Übersichten) */
const UEBERSICHTEN = new Set(["alle", "eigene", "geteilt", "andere"]);

export function Tastenkuerzel({ offen, onSchliessen }: { offen: boolean; onSchliessen: () => void }) {
  const tel = useTelefonie();
  const meldung = useMeldung();
  const ort = useLocation();
  // Die Listener hängen einmal; der jeweils aktuelle Stand kommt über Referenzen
  const telRef = useRef(tel);
  telRef.current = tel;
  const meldungRef = useRef(meldung);
  meldungRef.current = meldung;
  const pfadRef = useRef(ort.pathname);
  pfadRef.current = ort.pathname;

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || !(e.metaKey || e.ctrlKey) || e.altKey) return;
      const k = e.key.toLowerCase();
      // ⌘⇧L bzw. Strg+Umschalt+L: Neuer Lead (wirkt auch beim Tippen, nicht aber unter offenen Dialogen)
      if (e.shiftKey && k === "l") {
        if (ebene.offen()) return;
        e.preventDefault();
        neuerLeadOeffnen();
        return;
      }
      const t = telRef.current;
      const imAnruf = !!t.aktiverAnruf && (t.status === "verbindet" || t.status === "klingelt" || t.status === "im_gespraech");
      // ⌘. : Auflegen. Ohne Anruf pausiert ein laufender Power Dialer. Gilt auch unter offenen Dialogen (Hörer in der Hand).
      if (!e.shiftKey && k === ".") {
        e.preventDefault();
        if (imAnruf) {
          void t.auflegen();
        } else if (t.dialer && !t.dialer.sitzung.beendet && !t.dialer.pausiert) {
          t.dialerPause(true);
          meldungRef.current.ok("Power Dialer pausiert. ⌘⇧X macht weiter.");
        }
        return;
      }
      // ⌘⇧X : Power Dialer starten (auf einer Smart View) oder nächster Anruf (im Dialer)
      if (e.shiftKey && k === "x") {
        e.preventDefault();
        if (t.dialer && !t.dialer.sitzung.beendet) {
          void t.dialerWeiter();
          return;
        }
        if (t.status === "nicht_eingerichtet") {
          meldungRef.current.zeige("info", t.grund ?? "Telefonie ist nicht bereit.");
          return;
        }
        const m = /^\/smart-views\/([^/]+)/.exec(pfadRef.current);
        const viewId = m ? decodeURIComponent(m[1]) : null;
        if (viewId && !UEBERSICHTEN.has(viewId)) {
          if (imAnruf) {
            meldungRef.current.zeige("info", "Es läuft schon ein Anruf. Mit ⌘. auflegen.");
            return;
          }
          void t.dialerStarten(viewId, { modus: "weiter" });
        } else {
          meldungRef.current.zeige("info", "Zum Starten des Power Dialers eine Smart View öffnen.");
        }
      }
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, []);

  return (
    <Dialog titel="Tastenkürzel" offen={offen} onSchliessen={onSchliessen} breite={560}>
      <div className="tasten-liste">
        {LISTE.map(([titel, zeilen]) => (
          <section key={titel}>
            <h3>{titel}</h3>
            <dl>
              {zeilen.map(([taste, text]) => (
                <div key={`${titel}-${taste}`} className="tasten-zeile">
                  <dt>
                    <kbd>{taste}</kbd>
                  </dt>
                  <dd>{text}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
